// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ConnectViewer from 'app/common/react/components/common/ConnectViewer/ConnectViewer';
import i18n from 'app/common/lib/i18n';
import throttle from 'lodash/throttle';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { State as UserType } from 'app/common/redux/reducers/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import User from 'app/common/react/components/common/User/User';
import { compose } from 'redux';
import { Spinner } from 'gestalt';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { getElementHeight, getScrollPos, getScrollHeight } from 'app/common/lib/react/scrollUtils';

const SCROLL_BUFFER = 50;
const SCROLL_THROTTLE_TIMEOUT = 10;

type OwnProps = {|
  id: string,
|};

type HOCProps = {|
  feedResource: Resource<Array<UserType>>,
|};

type ReduxProps = {|
  users: { [id: string]: UserType },
|};

type Props = {| ...OwnProps, ...HOCProps, ...ReduxProps |};

export class LikedByList extends Component<Props> {
  componentWillUnmount() {
    this.list.removeEventListener('scroll', this.handleScroll);
  }

  setRef = (el: ?HTMLElement) => {
    if (el && !this.list) {
      this.list = el.parentElement;
      if (this.list) {
        this.list.addEventListener('scroll', this.handleScroll);
      }
    }
  };

  fetchMore = () => {
    const { feedResource } = this.props;
    if (!feedResource.isFetching) {
      if (feedResource.isAtEnd) {
        window.removeEventListener('scroll', this.handleScroll);
      } else {
        feedResource.fetchMore();
      }
    }
  };

  handleScroll = throttle(() => {
    const { feedResource } = this.props;
    if (feedResource.isFetching || feedResource.isAtEnd) {
      return;
    }

    const containerHeight = getElementHeight(this.list);
    const scrollHeight = getScrollHeight(this.list);
    const scrollPos = getScrollPos(this.list);

    if (scrollHeight - scrollPos - SCROLL_BUFFER < containerHeight) {
      this.fetchMore();
    }
  }, SCROLL_THROTTLE_TIMEOUT);

  list: any;

  render() {
    const {
      feedResource: { data, isLoaded, isFetching },
      users: reduxUsers,
    } = this.props;
    if (isLoaded && data && data.length > 0 && !isFetching) {
      const users = data.map((user) => (user.id ? { ...user, ...reduxUsers[user.id] } : user));
      return (
        <ConnectViewer>
          {(viewer: Viewer) => (
            <div style={{ maxHeight: '400px' }} ref={this.setRef} data-test-id="liked-by-list">
              {users.map((user: UserType) => (
                <User
                  isSelf={Boolean(viewer.id && user.id === viewer.id)}
                  className="p2 communityItem"
                  key={user.id}
                  showFollow
                  showName
                  size={'medium'}
                  // $FlowFixMe incompatible user types
                  user={user}
                  viewType={'thumb'}
                />
              ))}
            </div>
          )}
        </ConnectViewer>
      );
    }

    return (
      <div className="loadingSpinner">
        <Spinner
          accessibilityLabel={i18n._(
            'Loading spinner for like list',
            'Accessibility label for loading spinner for like list',
          )}
          show
        />
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState, props: Props): ReduxProps => ({
  users: state.users,
});

export const AggregatedCommentLikedByList = compose(
  withResource(
    {
      name: 'AggregatedCommentLikedByResource',
      options: (props) => ({
        aggregatedCommentId: props.id,
      }),
      key: 'feedResource',
    },
    { allowStale: false },
  ),
  connect(mapStateToProps),
)(LikedByList);

export const DidItLikedByList = compose(
  withResource(
    {
      name: 'DidItLikedByResource',
      options: (props) => ({
        didItDataId: props.id,
      }),
      key: 'feedResource',
    },
    { allowStale: false },
  ),
  connect(mapStateToProps),
)(LikedByList);
